import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ChevronDown, Menu, X } from 'lucide-react';
import '../../i18n';
import './LandingV2.css';

const LOGO = '/images/askeet_logo_light_bg_webp.webp';
const WP_URL = 'https://fr.wordpress.org/plugins/askeet/';

/**
 * Shared V2 chrome (nav + footer) used by every page so the blue/indigo
 * design is applied site-wide. When `isHome` is true, the section links
 * smooth-scroll within the page; otherwise they navigate back to the home
 * page (root) and scroll to the target section on load.
 */
export default function V2Layout({ children, isHome = false }) {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Navigate to an in-page section. On the home page we scroll smoothly;
  // on other pages we send the user home with the section in the hash.
  const goSection = (e, id) => {
    e.preventDefault();
    setMenuOpen(false);
    if (isHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = `/#${id}`;
    }
  };

  const goHome = (e) => {
    if (isHome) {
      e.preventDefault();
      setMenuOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // otherwise let the browser navigate to "/"
  };

  return (
    <div className="v2-page">
      {/* ---------------- NAV ---------------- */}
      <nav className="v2-nav">
        <div className="v2-container v2-nav-inner">
          <a href="/" className="v2-nav-logo" onClick={goHome}>
            <img src={LOGO} alt="Askeet.ai" />
          </a>
          <div className="v2-nav-links">
            <a href="/#domaines" onClick={(e) => goSection(e, 'domaines')}>{t('nav.domains')}</a>
            <a href="/#features" onClick={(e) => goSection(e, 'features')}>{t('nav.features')}</a>
            <a href="/#integrations" onClick={(e) => goSection(e, 'integrations')}>{t('nav.integrations')}</a>
            <a href="/#pricing" onClick={(e) => goSection(e, 'pricing')}>{t('nav.pricing')}</a>
            <a href="/#demo" onClick={(e) => goSection(e, 'demo')}>{t('demo.title')}</a>
          </div>
          <div className="v2-nav-right">
            <div className="v2-lang">
              <select value={i18n.language?.startsWith('fr') ? 'fr' : 'en'} onChange={(e) => i18n.changeLanguage(e.target.value)} aria-label="Language">
                <option value="en">EN</option>
                <option value="fr">FR</option>
              </select>
              <ChevronDown />
            </div>
            <a className="v2-btn v2-btn-primary" href={WP_URL} target="_blank" rel="noreferrer">
              {t('hero.cta')} <ArrowRight />
            </a>
            <button className="v2-burger" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu" aria-expanded={menuOpen}>
              {menuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        <div className={`v2-mobile-menu ${menuOpen ? 'open' : ''}`}>
          <a onClick={(e) => goSection(e, 'domaines')}>{t('nav.domains')}</a>
          <a onClick={(e) => goSection(e, 'features')}>{t('nav.features')}</a>
          <a onClick={(e) => goSection(e, 'integrations')}>{t('nav.integrations')}</a>
          <a onClick={(e) => goSection(e, 'pricing')}>{t('nav.pricing')}</a>
          <a onClick={(e) => goSection(e, 'demo')}>{t('demo.title')}</a>
          <a className="v2-btn v2-btn-primary" href={WP_URL} target="_blank" rel="noreferrer" onClick={() => setMenuOpen(false)}>
            {t('hero.cta')} <ArrowRight />
          </a>
        </div>
      </nav>

      {/* ---------------- PAGE CONTENT ---------------- */}
      {children}

      {/* ---------------- FOOTER ---------------- */}
      <footer className="v2-footer">
        <div className="v2-container">
          <div className="v2-footer-grid">
            <div className="v2-footer-logo">
              <a href="/" onClick={goHome}>
                <img src={LOGO} alt="Askeet.ai" />
              </a>
              <p className="desc">{t('footer.description')}</p>
            </div>
            <div>
              <h5>{t('footer.product')}</h5>
              <ul>
                <li><a href="/#features" onClick={(e) => goSection(e, 'features')}>{t('nav.features')}</a></li>
                <li><a href="/#integrations" onClick={(e) => goSection(e, 'integrations')}>{t('nav.integrations')}</a></li>
                <li><a href="/#domaines" onClick={(e) => goSection(e, 'domaines')}>{t('nav.domains')}</a></li>
              </ul>
            </div>
            <div>
              <h5>{t('footer.legal')}</h5>
              <ul>
                <li><a href="#mentions">{t('footer.legal_mentions')}</a></li>
                <li><a href="#privacy">{t('footer.legal_privacy')}</a></li>
              </ul>
            </div>
            <div>
              <h5>{t('footer.support')}</h5>
              <ul>
                <li><a href={WP_URL} target="_blank" rel="noreferrer">{t('hero.cta')}</a></li>
                <li><a href="/" onClick={goHome}>{t('nav.home')}</a></li>
              </ul>
            </div>
          </div>
          <div className="v2-footer-bottom">
            <span>© {new Date().getFullYear()} {t('footer.company')}. {t('footer.copyright')}</span>
            <span>Reach Technologies</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export { WP_URL, LOGO };
